import { Editor, Element, Node, NodeEntry, Text, Transforms, Path } from 'slate';

import { CustomEditor, CustomText, GroupingOperatorElement } from '../types';
import { isChipPattern } from './utils';

// ##############################################################################################
// [PURPOSE]: Imperitive handlers, used for cleanup - and importantly, are only called before submitting the query.
// They cannot be used in the normalization loop as they will break the normalization loop.
// ##############################################################################################

// [PURPOSE]: Handles paragraphs that contain a meaningful text node, converting them to simple chips if appropriate.
export const handleLoneTextNode = (editor: CustomEditor) => {
  if (!editor) return;
  const { selection } = editor;
  if (!selection) return;

  // Get all paragraphs in the editor
  const paragraphs = Editor.nodes(editor, {
    match: (n) => Element.isElement(n) && n.type === 'paragraph',
  });

  Editor.withoutNormalizing(editor, () => {
    // Process each paragraph
    for (const [paragraph, paragraphPath] of paragraphs) {
      if (!Element.isElement(paragraph)) continue;

      // Check if there's only one meaningful child (non-whitespace)
      let meaningfulTextNode: NodeEntry<Text> | null = null;

      // Iterate through children to check content
      for (const [child, childPath] of Node.children(editor, paragraphPath)) {
        if (Text.isText(child) && child.text.trim() !== '') {
          if (meaningfulTextNode) {
            break;
          }
          meaningfulTextNode = [child, childPath];
        }
      }

      // If the only meaningful content is a single non-empty text node,
      // mark it for conversion to a simple chip
      if (meaningfulTextNode) {
        const [textNode, textPath] = meaningfulTextNode;

        // Skip if text is already marked for conversion
        if ((textNode as CustomText).convertToSimpleChip) continue;

        // Skip if text appears to be a complex pattern like a chip
        if (isChipPattern(textNode.text)) continue;

        // Mark for conversion
        Transforms.setNodes(editor, { convertToSimpleChip: true }, { at: textPath });
      }
    }
  });

  // Force normalization after all changes
  Editor.normalize(editor, { force: true });
};

export const handleClosingGroupingParenthesis = (editor: CustomEditor) => {
  if (!editor) return;
  const { selection } = editor;
  if (!selection) return;

  const paragraphs = Editor.nodes(editor, {
    match: (n) => Element.isElement(n) && n.type === 'paragraph',
  });

  Editor.withoutNormalizing(editor, () => {
    // For each paragraph, check the balance of parentheses
    for (const [paragraph, paragraphPath] of paragraphs) {
      if (!Element.isElement(paragraph)) continue;

      // Track opening and closing grouping operators
      const openingOperators: Path[] = [];
      const closingOperators: Path[] = [];

      // Scan all children to find opening and closing parentheses
      for (const [child, childPath] of Node.children(editor, paragraphPath)) {
        if (Element.isElement(child) && child.type === 'grouping-operator') {
          if (child.operator === '(') {
            openingOperators.push(childPath);
          } else if (child.operator === ')') {
            closingOperators.push(childPath);
          }
        } else if (Element.isElement(child) && child.type === 'paragraph') {
          // For group elements, check the first child (should be opening parenthesis)
          // and last child (should be closing parenthesis)
          let hasOpeningParen = false;
          let hasClosingParen = false;

          for (const [groupChild, groupChildPath] of Node.children(editor, childPath)) {
            if (Element.isElement(groupChild) && groupChild.type === 'grouping-operator') {
              if (groupChild.operator === '(') {
                hasOpeningParen = true;
                openingOperators.push(groupChildPath);
              } else if (groupChild.operator === ')') {
                hasClosingParen = true;
                closingOperators.push(groupChildPath);
              }
            }
          }

          // If the group has an opening parenthesis but no closing one, add a closing one
          if (hasOpeningParen && !hasClosingParen) {
            const closerNode = {
              children: [{ text: '' }],
              operator: ')',
              type: 'grouping-operator',
            } as GroupingOperatorElement;

            // Insert at the end of the group
            const groupChildren = [...Node.children(editor, childPath)];
            const insertPath = [...childPath, groupChildren.length];
            Transforms.insertNodes(editor, closerNode, { at: insertPath });
          }
        }
      }

      // Check if we have more opening parentheses than closing ones
      if (openingOperators.length > closingOperators.length) {
        const missingClosingCount = openingOperators.length - closingOperators.length;

        // Create and insert missing closing parentheses at the end of the paragraph
        for (let i = 0; i < missingClosingCount; i++) {
          const closerNode = {
            children: [{ text: '' }],
            operator: ')',
            type: 'grouping-operator',
          } as GroupingOperatorElement;

          const paragraphChildren = [...Node.children(editor, paragraphPath)];
          const insertPath = [...paragraphPath, paragraphChildren.length];
          Transforms.insertNodes(editor, closerNode, { at: insertPath });
        }
      }
    }

    // Move selection to the end of the EDITOR after insertion
    Transforms.select(editor, Editor.end(editor, [])); // Select end of the whole editor
    Transforms.collapse(editor, { edge: 'end' });
  });
};

// [PURPOSE]: Ensures that the text node where the cursor is located ends with a space, adding one if necessary. This is useful for separating automatically converted elements (like chips) from subsequent text.
export const handleAddingTrailingSpace = (editor: CustomEditor) => {
  if (!editor) return;
  const { selection } = editor;
  if (!selection) return;

  // Get the current node at the selection anchor
  const [node, path] = Editor.node(editor, selection.anchor.path);

  // Make sure it's a text node
  if (!Text.isText(node)) return;

  const chipPattern = isChipPattern(node.text);
  if (chipPattern) {
    Transforms.setNodes(editor, { convertToChip: true }, { at: path });

    return;
  } else {
    // Check if the text already ends with a space
    if (node.text.endsWith(' ')) return;

    Editor.withoutNormalizing(editor, () => {
      // Add a space at the end of the current text node
      Transforms.insertText(editor, ' ', {
        at: {
          offset: node.text.length,
          path,
        },
      });
    });
  }
};

// [PURPOSE]: Automatically adds a closing parenthesis to a text node if it contains an opening parenthesis but no closing one, and cleans up trailing commas/whitespace before adding it.
export const handleUnclosedParenthesis = (editor: CustomEditor) => {
  if (!editor) return;
  const { selection } = editor;
  if (!selection) return;

  // Get the node at the selection's anchor
  const [node, path] = Editor.node(editor, selection.anchor.path);

  // Ensure it's a text node containing '(' but not ')'
  if (!Text.isText(node) || !node.text.includes('(') || node.text.includes(')')) {
    return;
  }

  const openParenIndex = node.text.indexOf('(');
  // Ensure there's actually an opening parenthesis found (should be true based on above check, but good practice)
  if (openParenIndex === -1) {
    return;
  }

  // Check if there's meaningful text *after* the opening parenthesis
  const textAfterOpenParen = node.text.slice(Math.max(0, openParenIndex + 1));
  if (textAfterOpenParen.trim().length === 0) {
    // No meaningful text after '(', so don't add a closing one yet
    return;
  }

  // Delegate to handleChipPatternDetection to handle the conversion (and adding closing paren)
  Transforms.setNodes(editor, { convertToChip: true }, { at: path });

  // Move selection to the end of the EDITOR after insertion
  Transforms.select(editor, Editor.end(editor, [])); // Select end of the whole editor
  Transforms.collapse(editor, { edge: 'end' });
};

export const fireImperitiveHandlers = (editor: CustomEditor) => {
  handleUnclosedParenthesis(editor);
  Editor.normalize(editor, { force: true });

  handleClosingGroupingParenthesis(editor);
  Editor.normalize(editor, { force: true });

  handleAddingTrailingSpace(editor);
  Editor.normalize(editor, { force: true });

  handleLoneTextNode(editor);
  Editor.normalize(editor, { force: true });
};
